import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pill, Check, X, Minus } from "lucide-react";
import { toast } from "sonner";
import { MedicineForm } from "@/components/MedicineForm";
import { DoseDetail } from "@/components/DoseDetail";
import {
  api,
  cachedGet,
  getFresh,
  todayStr,
  fmt12,
  shiftDate,
  nowHM,
  effectiveStatus,
  fmtDayLabel,
} from "@/lib/api";

const WEEKS_BACK = 25;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const iso = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const buildWeeks = () => {
  const end = new Date();
  end.setDate(end.getDate() + (6 - end.getDay())); // Saturday of this week
  const start = new Date(end);
  start.setDate(start.getDate() - (WEEKS_BACK * 7 + 6));
  const weeks = [];
  for (let w = 0; w < WEEKS_BACK + 1; w++) {
    const days = [];
    for (let d = 0; d < 7; d++) {
      const cur = new Date(start);
      cur.setDate(start.getDate() + w * 7 + d);
      days.push(iso(cur));
    }
    weeks.push(days);
  }
  return { weeks, start: iso(start), end: iso(end) };
};

const CELL = {
  taken: "bg-emerald-400/85 text-slate-900 font-semibold",
  skipped: "bg-red-400/80 text-slate-900 font-semibold",
  snoozed: "bg-amber-400/80 text-slate-900 font-semibold",
  none: "bg-slate-800/70 text-slate-500",
  blank: "bg-slate-900/50 text-slate-500",
  off: "bg-slate-900/25 text-slate-600",
};

const scheduledOn = (cal, date) => {
  const dow = new Date(date + "T12:00:00").getDay();
  const week = Array.isArray(cal.week_days) ? cal.week_days : [0, 1, 2, 3, 4, 5, 6];
  if (!week.includes(dow)) return false;
  const begin = cal.start_date || cal.created_at?.slice(0, 10);
  if (begin && date < begin) return false;
  if (cal.end_date && date > cal.end_date) return false;
  return true;
};

const Mark = ({ status, testid }) => {
  if (status === "taken") return <Check data-testid={testid} className="mx-auto h-[18px] w-[18px] text-emerald-400" />;
  if (status === "skipped") return <X data-testid={testid} className="mx-auto h-[18px] w-[18px] text-red-400" />;
  if (status === "snoozed") return <Minus data-testid={testid} className="mx-auto h-[18px] w-[18px] text-amber-400" />;
  return <Minus data-testid={testid} className="mx-auto h-[18px] w-[18px] text-slate-600" />;
};

const WeekView = ({ cal, onPick }) => {
  const today = todayStr();
  const dates = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) out.push(shiftDate(today, -i));
    return out; // today first
  }, [today]);

  return (
    <div className="mb-4 rounded-2xl border border-slate-800 bg-slate-900/45 p-3" data-testid="cal-week-view">
      <p className="mb-2 text-[14px] font-semibold text-slate-200">Last 7 days</p>
      <table className="w-full table-fixed">
        <thead>
          <tr>
            <th className="w-[62px] pb-1.5 text-left text-[12px] font-medium text-slate-500">Time</th>
            {dates.map((d) => (
              <th
                key={d}
                className={`pb-1.5 text-center text-[12px] font-medium ${
                  d === today ? "text-sky-300" : "text-slate-500"
                }`}
              >
                {d === today ? "Today" : DOW[new Date(d + "T12:00:00").getDay()]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cal.times.map((t) => (
            <tr key={t}>
              <td className="py-1.5 text-left text-[13px] text-slate-400">{fmt12(t)}</td>
              {dates.map((d) => (
                <td key={d} className="py-1.5">
                  {scheduledOn(cal, d) && d <= today ? (
                    <button
                      onClick={() => onPick(d, t)}
                      className="w-full rounded-md py-0.5 transition-colors duration-150 active:bg-slate-800/60"
                      data-testid={`week-cell-${d}-${t}`}
                    >
                      <Mark status={cal.per_time?.[d]?.[t] || "none"} testid={`week-mark-${d}-${t}`} />
                    </button>
                  ) : (
                    <span className="block text-center text-[13px] text-slate-700">·</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TimeGrid = ({ time, cal, weeks, showLabel, onPick }) => {
  const scroller = useRef(null);
  const today = todayStr();

  useEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, [cal]);

  const state = (date) => {
    const logged = cal.per_time?.[date]?.[time];
    if (logged) return logged; // history stays visible even if the schedule changed later
    if (!scheduledOn(cal, date)) return "off";
    const begin = cal.start_date || cal.created_at?.slice(0, 10);
    if (date > today || (begin && date < begin)) return "blank";
    return "none";
  };

  const monthLabel = (week) => {
    const firstOfMonth = week.find((d) => d.slice(-2) === "01");
    if (!firstOfMonth) return "";
    return new Date(firstOfMonth + "T12:00:00").toLocaleDateString(undefined, { month: "short" });
  };

  return (
    <div className="mb-4 rounded-2xl border border-slate-800 bg-slate-900/45 p-3">
      {showLabel && <p className="mb-2 text-[14px] text-slate-400">{fmt12(time)}</p>}
      <div className="flex gap-4">
        <div ref={scroller} className="flex-1 overflow-x-auto pb-1" data-testid={`cal-scroller-${time}`}>
          <div className="inline-block min-w-full pr-1">
            <div className="mb-1 flex gap-1">
              {weeks.map((week, wi) => (
                <span key={wi} className="w-[30px] shrink-0 text-[11px] text-slate-500">
                  {monthLabel(week)}
                </span>
              ))}
            </div>
            {[0, 1, 2, 3, 4, 5, 6].map((dow) => (
              <div key={dow} className="mb-1 flex gap-1">
                {weeks.map((week, wi) => {
                  const date = week[dow];
                  const st = state(date);
                  const actionable = st !== "off" && date <= today;
                  return (
                    <button
                      key={wi}
                      disabled={!actionable}
                      onClick={() => onPick(date, time)}
                      data-testid={`cal-cell-${time}-${date}`}
                      className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md text-[11px] transition-transform duration-150 ${
                        CELL[st]
                      } ${date === today ? "ring-1 ring-sky-400" : ""} ${actionable ? "active:scale-90" : ""}`}
                    >
                      {Number(date.slice(-2))}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="shrink-0 pt-[18px]">
          {DOW.map((d) => (
            <p key={d} className="mb-1 flex h-[30px] items-center text-[11px] text-slate-500">
              {d}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function MedicineCalendar() {
  const { medId } = useParams();
  const navigate = useNavigate();
  const { weeks, start, end } = useMemo(buildWeeks, []);
  const [cal, setCal] = useState(null);
  const [picked, setPicked] = useState(null);
  const [med, setMed] = useState(() => (cachedGet("/medicines") || []).find((m) => m.med_id === medId) || null);
  const [groups, setGroups] = useState(() => cachedGet("/groups")?.groups || []);

  const loadGroups = () =>
    getFresh("/groups")
      .then((d) => setGroups(d.groups))
      .catch(() => {});

  const applyList = (list) => {
    setMed(list.find((m) => m.med_id === medId) || null);
  };

  const loadCal = () => {
    api
      .get(`/medicines/${medId}/calendar`, { params: { start, end } })
      .then(({ data }) => setCal(data))
      .catch(() => setCal(null));
  };

  const openDose = async (date, time) => {
    const today = todayStr();
    if (date > today) return;
    try {
      const list = await getFresh("/schedule", { date });
      const it = list.find((x) => x.med_id === medId && x.time === time);
      if (it) setPicked({ date, item: it });
      else toast.error("That dose isn't scheduled on this day");
    } catch {
      toast.error("Could not load that dose");
    }
  };

  const act = async (status, extra = {}) => {
    if (!picked) return;
    try {
      await api.post("/doses", {
        med_id: medId,
        date: picked.date,
        time: picked.item.time,
        status,
        now: nowHM(),
        ...extra,
      });
      setPicked(null);
      loadCal();
    } catch {
      toast.error("Could not save that change");
    }
  };

  useEffect(() => {
    loadCal();
    loadGroups();
    getFresh("/medicines")
      .then(applyList)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medId, start, end]);

  return (
    <div className="grain relative min-h-screen bg-[#0B1120] text-slate-100">
      <div className="aurora" />
      <div className="relative z-10 mx-auto min-h-screen max-w-md px-4 pb-10">
        <header className="sticky top-0 z-20 -mx-4 mb-3 flex items-center gap-2 border-b border-slate-800 bg-slate-950/80 px-4 py-1.5 backdrop-blur-xl">
          <button
            data-testid="cal-back-btn"
            onClick={() => navigate("/compliance")}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-800 active:scale-90"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[17px] font-bold tracking-tight" data-testid="cal-med-name">
            {cal?.name || med?.name || "Medicine"}
          </h1>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-sky-500/30 bg-sky-500/10">
            <Pill className="h-4 w-4 text-sky-300" />
          </span>
        </header>

        {!cal ? (
          <div className="h-64 animate-pulse rounded-2xl bg-slate-900/60" />
        ) : (
          <>
            <WeekView cal={cal} onPick={openDose} />
            {cal.times.map((t) => (
              <TimeGrid
                key={t}
                time={t}
                cal={cal}
                weeks={weeks}
                showLabel={cal.times.length > 1}
                onPick={openDose}
              />
            ))}
            {med && (
              <div
                className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3"
                data-testid="cal-edit-medicine"
              >
                <p className="mb-2 text-[14px] font-semibold text-slate-200">Edit medicine</p>
                <MedicineForm
                  med={med}
                  groups={groups}
                  onGroupsChanged={() => {
                    loadGroups();
                    getFresh("/medicines").then(applyList);
                  }}
                  onSaved={({ keepOpen }) => {
                    loadCal();
                    getFresh("/medicines").then(applyList);
                    if (!keepOpen) navigate("/compliance");
                  }}
                  onDeleted={() => navigate("/compliance")}
                />
              </div>
            )}
          </>
        )}
      </div>

      {picked && (
        <DoseDetail
          item={picked.item}
          status={effectiveStatus(picked.item, picked.date)}
          hideSnooze={picked.date !== todayStr()}
          dateLabel={picked.date === todayStr() ? "Today" : fmtDayLabel(picked.date)}
          onAction={act}
          onClose={() => setPicked(null)}
        />
      )}
    </div>
  );
}
